// Dashboard screen showing configuration tabs.
import { ScrollView, StyleSheet, View } from "react-native";
import { useConfig } from "util/features";
import { Heading, UtilityText } from "./text";
import { HorizBox, HoverView, Pad, PadBox, VertBox, WindowTitle } from "./basics";
import { useDatastore, useInstanceKey, useStructureKey } from "util/datastore";
import { colorButtonPrimary, colorButtonSecondary, colorSurfacePrimary, colorSurfaceSecondary, colorTextPrimary } from "./color";
import { hoverColor, pressColor } from "util/util";
import { undefS, TLabel, t } from "util/stdtypes";
import { useHasCapability } from "./admin";
import { Banner } from "./banner";


export type DashboardTab = {
    key: string;
    label: TLabel;
    icon: React.ComponentType;
    component: React.ComponentType;
    testID?: string;
}

export type DashboardConfig = {
    dashboardTabs: DashboardTab[];
    dashboardTitleLabel: TLabel;
    dashboardCapability?: string;
}


export function NavButton({tab, selected}) {
    const s = NavButtonStyle;
    const datastore = useDatastore();
    const structureKey = useStructureKey();
    const instanceKey = useInstanceKey();

    function onPress() {
        datastore.gotoInstance({structureKey, instanceKey, params: {selectedTabKey: tab.key}, replace: true});
    }

    return <HoverView testID={tab.testID ?? tab.key} onPress={onPress}
        style={[s.tab, selected ? s.selectedTab : {}]}
        hoverStyle={s.hover} pressedStyle={s.pressed}
    >
        <tab.icon color={colorTextPrimary}/>
        <Pad size={12} />
        <UtilityText label={tab.label} />
    </HoverView>
}

const NavButtonStyle = StyleSheet.create({
    tab: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 44,
        paddingHorizontal: 12,
        borderRadius: 32,
        backgroundColor: colorButtonSecondary,
    },
    hover: {
        backgroundColor: hoverColor(colorButtonSecondary),
    },
    pressed: {
        backgroundColor: pressColor(colorButtonSecondary),
    },
    selectedTab: {
        backgroundColor: colorSurfaceSecondary,
        borderRadius: 32
    }
});

export function SideNav({selectedTabKey}) {
    const {dashboardTabs, dashboardTitleLabel} = useConfig<DashboardConfig>();
    return <PadBox horiz={40} vert={40}>
        <Heading label={dashboardTitleLabel} />
        <Pad size={40} />
        <VertBox gap={16} wide>
            {dashboardTabs.map((tab) => (
                <NavButton key={tab.key} tab={tab} selected={tab.key === selectedTabKey}/>
            ))}
        </VertBox>
    </PadBox>
}

export function DashboardSideNavScreen({selectedTabKey=undefS}) {
    const s = DashboardSideNavScreenStyle;
    const {dashboardTabs, dashboardTitleLabel, dashboardCapability} = useConfig<DashboardConfig>();
    const hasCapability = useHasCapability(dashboardCapability);
    if (!hasCapability) {
        return <Banner><UtilityText label={t('You do not have access to this feature')}/></Banner>
    }
    const selectedTab = dashboardTabs.find((tab) => tab.key === selectedTabKey) ?? dashboardTabs[0];
    return <View style={s.screen}>
        <SideNav selectedTabKey={selectedTabKey} />
        <ScrollView style={s.content}>    
            {selectedTab && <selectedTab.component />}
        </ScrollView>
    </View>
}

const DashboardSideNavScreenStyle = StyleSheet.create({
    screen: {
        flex: 1,
        flexDirection: 'row',
        backgroundColor: colorSurfacePrimary,
    },
    content: {
        backgroundColor: colorSurfaceSecondary,
        flex: 1,
        padding: 40,
    }
});
